import { t } from '@lingui/macro'
import { useState } from 'react'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { hasNavigableAddress } from '@/modules/shared/helpers/navigation'
import { formatAddressLines } from '../../helpers/address'
import { LocationCard } from '../location-card/LocationCard'

import type { Location } from '../../types/location.types'

type AgendaLocationCardProps = {
  location: Location
  /** Called when the patient asks to navigate; only offered when the location has an address or coordinates. */
  onNavigate: (location: Location) => void
}

/**
 * The location of an agenda item as the detail screens show it: photo when there is one, name and
 * address lines, and a navigate button when there is somewhere to navigate to. A plain component
 * rather than call-site composition because both detail screens arrange these parts identically.
 */
const AgendaLocationCard = ({ location, onNavigate }: AgendaLocationCardProps) => {
  // A photo that fails to load gives way to the card without a photo. Remembering which URL failed,
  // rather than a flag, gives a different photo a fresh chance.
  const [failedImageUrl, setFailedImageUrl] = useState<string>()

  const addressLines = formatAddressLines(location.address)
  const canNavigate = hasNavigableAddress(location.address) || Boolean(location.coordinates)
  const showImage = Boolean(location.imageUrl) && location.imageUrl !== failedImageUrl

  return (
    <LocationCard>
      {showImage ? (
        <LocationCard.Media>
          <LocationCard.Image
            src={location.imageUrl}
            alt=''
            decoding='async'
            referrerPolicy='no-referrer'
            onError={() => setFailedImageUrl(location.imageUrl)}
          />
        </LocationCard.Media>
      ) : null}
      <LocationCard.Content>
        <FlexColumn>
          {location.name ? <LocationCard.Name>{location.name}</LocationCard.Name> : null}
          {addressLines.map((line) => (
            <LocationCard.Address key={line}>{line}</LocationCard.Address>
          ))}
        </FlexColumn>
        {canNavigate ? <LocationCard.Icon icon={['fal', 'location-arrow']} /> : null}
      </LocationCard.Content>
      {/* Without an address or coordinates there is nothing to navigate to, so the card stays plain content */}
      {canNavigate ? (
        <LocationCard.Navigate
          onClick={() => onNavigate(location)}
          // The label interpolates the location so the accessible name carries both the
          // purpose and the address, rather than replacing one with the other.
          aria-label={t({
            id: 'agenda.detail.location.navigate',
            comment: 'Accessible label of the location card on the agenda detail screens',
            message: 'Navigeer naar {location}',
            values: { location: [location.name, ...addressLines].filter(Boolean).join(', ') },
          })}
        />
      ) : null}
    </LocationCard>
  )
}

export { AgendaLocationCard }
